<template>
  <div class="autobookpro-page-content">
    <div style="width: 90%" class="q-mx-auto q-mt-md">
      <div class="row justify-between items-center q-mb-sm">
        <app-select-input
          text="Category"
          v-model="status"
          :options="statusOptions"
          option-label="label"
          option-value="value"
        />
        <div>
          <q-btn
            v-if="canCreateStatus"
            color="primary"
            label="Create"
            :to="{
              name: 'create-status',
              query: { category: status.value }
            }"
          />
        </div>
      </div>
      <q-table
        class="q-mt-md"
        flat
        bordered
        :loading="loading"
        :data="statuses"
        :columns="columns"
        :rows-per-page-options="[10, 15, 20]"
        binary-state-sort
      >
        <template v-slot:body="props">
          <q-tr :props="props" draggable>
            <q-td key="name" :props="props">
              {{ props.row.name }}
            </q-td>
            <q-td key="sendEmail" :props="props">
              <div
                v-if="props.row.emailTemplate"
                class="text-bold text-secondary"
              >
                Yes
              </div>
              <div v-else class="text-dark">No</div>
            </q-td>
            <q-td key="emailTemplate" :props="props">
              {{ props.row.emailTemplate ? props.row.emailTemplate.name : '' }}
            </q-td>
            <q-td key="fullpayEmailTemplate" :props="props">
              {{ props.row.fullpayEmailTemplate ? props.row.fullpayEmailTemplate.name : '' }}
            </q-td>
            <q-td key="smsTemplate" :props="props">
              {{ props.row.smsTemplate ? props.row.smsTemplate.name : '' }}
            </q-td>
            <q-td key="fullpaySmsTemplate" :props="props">
              {{ props.row.fullpaySmsTemplate ? props.row.fullpaySmsTemplate.name : '' }}
            </q-td>
            <q-td key="vmName" :props="props">
              {{ props.row.vmName }}
            </q-td>
            <q-td key="fullpayVmName" :props="props">
              {{ props.row.fullpayVmName }}
            </q-td>
            <q-td key="children" :props="props">
              <div
                v-if="props.row.childrenCount > 0"
                class="text-info text-bold cursor-pointer"
                @click="props.expand = !props.expand"
              >
                {{ props.row.childrenCount }}
              </div>
              <div v-else>0</div>
            </q-td>
            <q-td key="options" :props="props">
              <q-btn
                v-if="canUpdateStatus"
                dense
                outline
                color="primary"
                size="sm"
                icon="edit"
                :to="{
                  name: 'status-detail',
                  params: { id: props.row.id },
                  query: { category: status.value }
                }"
              />
              <q-btn
                :disable="!canUpdateStatus || props.row.reserved"
                dense
                outline
                color="red"
                size="sm"
                icon="delete"
                @click="handleDeleteStatus(props.row.id)"
              />
              <q-btn-group flat style="flex-direction:column">
                <q-btn
                  icon="expand_less"
                  size="sm"
                  padding="none"
                  @click="swapRow(props.rowIndex,-1)"
                />
                <q-btn
                  icon="expand_more"
                  size="sm"
                  padding="none"
                  @click="swapRow(props.rowIndex,1)"
                />
              </q-btn-group>
            </q-td>
          </q-tr>
          <q-tr v-if="props.expand" :props="props">
            <q-td colspan="100%">
              <div class="q-mx-auto" style="width: 80%">
                <app-children-list
                  :statuses="props.row.children"
                  :category="status.value"
                  :canUpdateStatus="canUpdateStatus"
                  @swapRow="(cIndex,dir)=>swapRow(cIndex, dir, props.rowIndex)"
                  @delete="(childId) => handleDeleteStatus(props.row.id, childId)"
                />
              </div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </div>
  </div>
</template>

<script>
import graphql from '@client/graphql';
import AppSelectInput from '@client/components/form/select-input';
import { assessPermission } from '@server/lib/permission-helpers';
import AppChildrenList from './children-list';

const statusOptions = [
  {
    label: 'Quotes',
    value: 'QUOTE'
  },
  {
    label: 'Orders',
    value: 'ORDER'
  }
];

export default {
  name: 'AppStatuses',
  metaInfo: {
    title: 'AutoBook Pro - Statuses'
  },
  components: {
    AppSelectInput,
    AppChildrenList
  },
  data() {
    return {
      statuses: [],
      status: statusOptions[0],
      loading: false,
      columns: [
        {
          name: 'name',
          label: 'Name',
          align: 'left',
          sortable: true
        },
        {
          name: 'sendEmail',
          label: 'Send Email',
          align: 'left'
        },
        {
          name: 'emailTemplate',
          label: 'Email',
          align: 'left'
        },
        {
          name: 'fullpayEmailTemplate',
          label: 'Full Pay Email',
          align: 'left'
        },
        {
          name: 'smsTemplate',
          label: 'SMS',
          align: 'left'
        },
        {
          name: 'fullpaySmsTemplate',
          label: 'Full Pay SMS',
          align: 'left'
        },
        {
          name: 'vmName',
          label: 'VM',
          align: 'left'
        },
        {
          name: 'fullpayVmName',
          label: 'Full Pay VM',
          align: 'left'
        },
        {
          name: 'children',
          label: 'Children',
          align: 'center'
        },
        {
          name: 'options',
          label: '',
          align: 'center'
        }
      ],
      statusOptions
    };
  },
  apollo: {
    currentUser: {
      query: graphql.queries.currentUser
    },
    statuses: {
      query: graphql.queries.getParentStatus,
      variables() {
        return {
          type: this.status.value
        };
      },
      update: (data) => data.parentStatuses,
      watchLoading(isLoading) {
        this.loading = isLoading;
      },
      fetchPolicy: 'cache-and-network'
    }
  },
  computed: {
    canCreateStatus() {
      return assessPermission.status.canCreate(this.currentUser);
    },
    canUpdateStatus() {
      return assessPermission.status.canUpdate(this.currentUser);
    }
  },
  methods: {
    async swapRow(index, dir, parentIndex) {
      let dRows;
      let parentId = null;
      if (parentIndex === undefined) {
        dRows = this.statuses;
      } else {
        dRows = this.statuses[parentIndex].children;
        parentId = this.statuses[parentIndex].id;
      }
      const startRow = dRows[index];
      let newIndex;
      if (index === 0 && dir === -1) {
        newIndex = dRows.length - 1;
      } else if (index === dRows.length - 1 && dir === 1) {
        newIndex = 0;
      } else {
            newIndex = index + dir;
      }
      const endRow = dRows[newIndex];
      const statusFields = [
        'name',
        'parentId',
        'emailTemplateId',
        'smsTemplateId',
        'vmname',
        'fullpayEmailTemplateId',
        'fullpaySmsTemplateId',
        'fullpayVmName',
        'weight'
        ];
        const statusMapping = {
          emailTemplateId: 'emailTemplate',
          smsTemplateId: 'smsTemplate',
          fullpayEmailTemplateId: 'fullpayEmailTemplate',
          fullpaySmsTemplateId: 'fullpaySmsTemplate'
        };
      const startRowData = statusFields.reduce((obj2, key) => ({
        ...obj2,
        [key]: statusMapping[key] ? startRow[statusMapping[key]]?.id : startRow[key]
      }), {});
      const endRowData = statusFields.reduce((obj2, key) => ({
        ...obj2,
        [key]: statusMapping[key] ? endRow[statusMapping[key]]?.id : endRow[key]
      }), {});
      try {
        await this.$apollo.mutate({
          mutation: graphql.mutations.updateStatus,
          variables: {
            id: startRow.id,
            input: { ...startRowData, weight: newIndex + 1, parentId }
          },
          watchLoading(isLoading) {
            this.loading = isLoading;
          }
        });
        await this.$apollo.mutate({
          mutation: graphql.mutations.updateStatus,
          variables: { id: endRow.id, input: { ...endRowData, weight: index + 1, parentId } },
          watchLoading(isLoading) {
            this.loading = isLoading;
          }
        });
        [dRows[index], dRows[newIndex]] = [dRows[newIndex], dRows[index]];
        if (parentIndex === undefined) {
            this.statuses = dRows;
        } else {
          this.statuses[parentIndex].children = dRows;
        }
      } catch (error) {
        this.notifyNegative(error.message.replace('GraphQL error: ', ''));
      }
    },
    async handleDeleteStatus(id, childId = null) {
      try {
        await this.$apollo.mutate({
          mutation: graphql.mutations.deleteStatus,
          variables: { id: childId || id },
          update: (store, { data: { deleteStatus } }) => {
            const data = store.readQuery({
              query: graphql.queries.getParentStatus,
              variables: {
                type: this.status.value
              }
            });
            if (childId) {
                const parentSt = data.parentStatuses.find(
                (e) => e.id === id
              );
              if (parentSt) {
                parentSt.children = parentSt.children.filter(
                  (e) => e.id !== deleteStatus.id
                );
                parentSt.childrenCount -= 1;
              }
            } else {
              data.parentStatuses = data.parentStatuses.filter(
                (e) => e.id !== deleteStatus.id
              );
            }
            store.writeQuery({
              query: graphql.queries.getParentStatus,
              variables: { type: this.status.value },
              data
            });
          }
        });

        this.notifyPositive('Successfully status has been removed!');
      } catch (error) {
        this.notifyNegative(error.message.replace('GraphQL error: ', ''));
      }
    }
  }
};
</script>
<style lang="scss"></style>
